import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Edit as EditIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericFormModal from '../form/GenericFormModal';
import { useContacts, useCreateContact, useUpdateContact } from '@/hooks/api/useContacts';
import { useNotification } from '@/contexts/NotificationContext';

interface LocationContactTableProps {
  locationId: string;
}

const LocationContactTable: React.FC<LocationContactTableProps> = ({ locationId }) => {
  const { data: contactsData } = useContacts();
  const createContactMutation = useCreateContact();
  const updateContactMutation = useUpdateContact();
  const { showSuccess, showError } = useNotification();
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);

  const contacts = useMemo(() => {
    if (!contactsData) return [];
    const contactsList = Array.isArray(contactsData) ? contactsData : Object.values(contactsData);
    return contactsList.filter((c: any) => c.locationId === locationId);
  }, [contactsData, locationId]);

  const contactFields = [
    {
      groupName: "Contact Information",
      fields: [
        {
          label: "First Name",
          name: "name",
          type: GENERIC_FORM_FIELD_TYPE.TEXT,
          required: true,
          columns: 6,
        },
        {
          label: "Last Name",
          name: "lastName",
          type: GENERIC_FORM_FIELD_TYPE.TEXT,
          required: true,
          columns: 6,
        },
        {
          label: "Email",
          name: "email",
          type: GENERIC_FORM_FIELD_TYPE.EMAIL,
          required: true,
          columns: 12,
        },
        {
          label: "Primary Contact",
          name: "isPrimary",
          type: GENERIC_FORM_FIELD_TYPE.CHECKBOX,
          required: false,
          columns: 12,
        },
      ],
    },
  ];

  const handleContactSubmit = async (values: any) => {
    console.log('Submitting contact values:', values);
    if (selectedContact) {
      updateContactMutation.mutate(
        {
          id: selectedContact.id,
          ...values,
          locationId: locationId
        },
        {
          onSuccess: () => {
            console.log('Contact updated, refreshing...');
            setContactModalVisible(false);
            setSelectedContact(null);
            showSuccess('Contact updated successfully');
          },
          onError: (error: any) => {
            showError(error.message || 'Failed to update contact');
          },
        }
      );
    } else {
      createContactMutation.mutate(
        {
          ...values,
          locationId: locationId
        },
        {
          onSuccess: () => {
            setContactModalVisible(false);
            setSelectedContact(null);
            showSuccess('Contact created successfully');
          },
          onError: (error: any) => {
            showError(error.message || 'Failed to create contact');
          },
        }
      );
    }
  };

  const contactColumns = [
    {
      accessorKey: 'name',
      header: 'First Name',
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'isPrimary',
      header: 'Primary Contact',
      Cell: ({ row }: any) => {
        console.log('Row data:', row.original);
        return row.original.isPrimary ? 'Yes' : 'No'
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        return (
          <IconButton
            onClick={() => {
              setSelectedContact(row.original);
              setContactModalVisible(true);
            }}
            size="small"
          >
            <EditIcon />
          </IconButton>
        );
      },
    },
  ];

  const renderContactsToolbar = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          p: '0.5rem',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">Contacts</Typography>
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedContact(null);
            setContactModalVisible(true);
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <GenericFormModal
        visible={contactModalVisible}
        onDismiss={() => {
          setContactModalVisible(false);
          setSelectedContact(null);
        }}
        onSubmit={handleContactSubmit}
        initialValues={selectedContact ? {
          id: selectedContact.id,
          name: selectedContact.name,
          lastName: selectedContact.lastName,
          email: selectedContact.email,
          isPrimary: selectedContact.isPrimary
        } : {}}
        fieldGroups={contactFields}
        title={selectedContact ? 'Edit Contact' : 'Create Contact'}
      />

      <Box sx={{ mt: 2 }}>
        <MaterialReactTable
          columns={contactColumns}
          data={contacts}
          enableColumnFilters={false}
          enableColumnActions={false}
          enableSorting={true}
          enableTopToolbar={true}
          enableBottomToolbar={true}
          enablePagination={true}
          muiTableBodyRowProps={{ hover: true }}
          renderTopToolbarCustomActions={renderContactsToolbar}
          state={{ isLoading: !contacts }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </>
  );
};

export default LocationContactTable; 